/**
 * 联系管理页面组件
 *
 * 展示通过网站联系表单提交的留言列表，支持分页浏览。
 * 每条记录包含提交时间、姓名、邮箱、主题、消息内容和已读/未读状态。
 *
 * 自定义元素：`crm-contacts-page`
 * 路由：`/crm/contacts`
 *
 * 依赖模块：
 * - `contactApi`：后端联系表单 API
 * - `BaseElement`：Lit 基类
 * - `crm-table-toolbar`：表格工具栏组件（刷新、总数显示）
 * - `crm-loading-overlay`：加载遮罩组件
 * - `crm-pagination`：分页组件
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { contactApi } from '../api.js';
import { BaseElement } from '../base-element.js';
import type { ContactSubmission } from '../types.js';
import { toast } from '../components/toast.js';
import { isNetworkError } from '../utils/index.js';
import '../components/table-toolbar.js';
import '../components/loading-overlay.js';
import '../components/pagination.js';

/**
 * 将 Unix 时间戳（秒）格式化为 "YYYY-MM-DD HH:mm" 字符串
 *
 * @param ts - Unix 时间戳（秒级）
 * @returns 格式化后的日期时间字符串
 */
function formatTimestamp(ts: number): string {
  const d = new Date(ts * 1000); // 后端时间戳为秒级，需乘以 1000 转毫秒
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

@customElement('crm-contacts-page')
export class CrmContactsPage extends BaseElement {
  /** 联系记录列表 */
  @state() private items: ContactSubmission[] = [];
  /** 加载状态标志 */
  @state() private isLoading = false;
  /** 列表请求序号，仅允许最后一次分页请求更新页面。 */
  private _listRequestId = 0;
  /** 总记录数（用于分页计算） */
  @state() private total = 0;
  /** 当前页码（从 1 开始） */
  @state() private currentPage = 1;
  /** 每页记录数 */
  @state() private perPage = 20;

  /** 空样式表（完全依赖 Tailwind 工具类） */
  static styles = css``;

  /**
   * 组件挂载时触发，加载数据
   */
  connectedCallback() {
    super.connectedCallback();
    this.loadContacts();
  }

  /**
   * 从后端加载当前页的联系记录
   *
   * 使用 contactApi.list 发起请求，更新 items、total 状态。
   * 网络错误时静默返回（由 isNetworkError 判断），其他错误显示 toast。
   *
   * @returns Promise<void>
   */
  private async loadContacts() {
    const requestId = ++this._listRequestId;
    this.isLoading = true;
    try {
      const r = await contactApi.list({ page: this.currentPage, per_page: this.perPage });
      if (requestId !== this._listRequestId) return;
      this.items = r.data;
      this.total = r.pagination.total;
    } catch (err: any) {
      if (requestId !== this._listRequestId) return;
      const msg = err instanceof Error ? err.message : String(err);
      if (isNetworkError(msg)) return; // 网络错误不弹 toast，避免重复提示
      toast.error(msg || '加载失败');
    } finally {
      if (requestId === this._listRequestId) this.isLoading = false;
    }
  }

  /**
   * 渲染联系管理页面
   *
   * 页面结构：
   * 1. 加载遮罩
   * 2. 页面标题 + 工具栏（总数、刷新按钮）
   * 3. 数据表格（提交时间、姓名、邮箱、主题、消息、状态）
   * 4. 分页组件
   *
   * @returns Lit 模板
   */
  render() {
    return html`
      <!-- 加载遮罩，isLoading 为 true 时显示 -->

      <!-- 页面标题 + 工具栏区域 -->
      <div class="mb-2 flex items-center gap-3">
        <h2 class="text-lg font-semibold text-text">联系管理</h2>
        <!-- crm-table-toolbar：显示总条数，刷新按钮触发 loadContacts -->
        <crm-table-toolbar
          .total=${this.total}
          .label=${'条'}
          .loading=${this.isLoading}
          @crm-toolbar-refresh=${() => this.loadContacts()}
        ></crm-table-toolbar>
      </div>

      <!-- 数据表格容器 -->
      <div class="bg-surface rounded-lg border border-border overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-border bg-white text-left">
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider w-40">提交时间</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider w-24">姓名</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider w-44">邮箱</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">主题</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider">消息</th>
                <th class="px-4 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wider w-20">状态</th>
              </tr>
            </thead>
            <tbody>
              ${this.items.length === 0
                ? html`<tr><td colspan="6" class="px-4 py-12 text-center text-text-muted text-sm">暂无数据</td></tr>`
                : this.items.map(item => html`
                <tr class="border-b border-border hover:bg-bg/50 transition-colors">
                  <td class="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">${formatTimestamp(item.created_at)}</td>
                  <td class="px-4 py-3 text-sm text-text">${item.name}</td>
                  <td class="px-4 py-3 text-sm text-text-secondary">${item.email}</td>
                  <td class="px-4 py-3 text-sm text-text">${item.subject}</td>
                  <!-- 消息超 80 字符时截断显示，title 属性提供完整内容的悬浮提示 -->
                  <td class="px-4 py-3 text-sm text-text-secondary max-w-xs truncate" title=${item.message}>
                    ${item.message.length > 80 ? item.message.slice(0, 80) + '...' : item.message}
                  </td>
                  <td class="px-4 py-3">
                    <!-- 状态徽章：已读为绿色，未读为琥珀色 -->
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${item.is_read ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}">
                      ${item.is_read ? '已读' : '未读'}
                    </span>
                  </td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
        <!-- 分页组件：页码切换时更新 currentPage 并重新加载 -->
        <crm-pagination
          .current=${this.currentPage}
          .perPage=${this.perPage}
          .total=${this.total}
          @page-change=${(e: CustomEvent) => { this.currentPage = e.detail.page; this.loadContacts(); }}
        ></crm-pagination>
      </div>
    `;
  }
}
